import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { SessionService } from './session.service';
import { APP_ROUTES, TOAST_KEY } from '../models/app.constants';

const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 1 hora de inactividad
const CHECK_INTERVAL_MS = 30 * 1000; // verificación cada 30s
const PERSIST_THROTTLE_MS = 30 * 1000; // guardado en localStorage máx. cada 30s

/**
 * Controla la sesión por INACTIVIDAD:
 *  - Cada interacción del usuario (mouse/teclado/scroll/touch) y cada petición
 *    HTTP renueva el tiempo de actividad.
 *  - Si pasan más de 1 hora sin actividad, se cierra la sesión.
 *  - Mientras el usuario siga usando el sistema, la sesión no vence: el
 *    authInterceptor renueva el access token de forma proactiva.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
    private static readonly LAST_ACTIVITY_KEY = 'dentsys_last_activity';

    private timer: ReturnType<typeof setInterval> | null = null;
    private lastActivity = 0;
    private lastPersisted = 0;
    private started = false;

    constructor(
        private sessionService: SessionService,
        private router: Router,
        private messageService: MessageService
    ) {}

    /** Activa el seguimiento de actividad (idempotente). */
    start(): void {
        this.lastActivity = this.readStored();
        if (!this.lastActivity) {
            this.persistActivity();
        }
        if (this.started) {
            return;
        }
        this.started = true;
        window.addEventListener('click', this.onEvent);
        window.addEventListener('keydown', this.onEvent);
        window.addEventListener('scroll', this.onEvent, { passive: true });
        window.addEventListener('touchstart', this.onEvent, { passive: true });
        window.addEventListener('mousemove', this.onEvent);
        this.timer = setInterval(() => this.check(), CHECK_INTERVAL_MS);
    }

    /** Detiene el seguimiento y limpia la actividad guardada. */
    stop(): void {
        this.started = false;
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
        window.removeEventListener('click', this.onEvent);
        window.removeEventListener('keydown', this.onEvent);
        window.removeEventListener('scroll', this.onEvent);
        window.removeEventListener('touchstart', this.onEvent);
        window.removeEventListener('mousemove', this.onEvent);
        localStorage.removeItem(IdleTimeoutService.LAST_ACTIVITY_KEY);
    }

    /** Marca actividad (llamado desde el interceptor HTTP en cada request). */
    updateActivity(): void {
        this.lastActivity = Date.now();
        this.persistActivity();
    }

    // ── Interno ──────────────────────────────────────────────────────────────

    private onEvent = () => {
        this.lastActivity = Date.now();
        if (Date.now() - this.lastPersisted > PERSIST_THROTTLE_MS) {
            this.persistActivity();
        }
    };

    private persistActivity(): void {
        this.lastPersisted = Date.now();
        localStorage.setItem(IdleTimeoutService.LAST_ACTIVITY_KEY, String(this.lastPersisted));
    }

    private readStored(): number {
        const raw = localStorage.getItem(IdleTimeoutService.LAST_ACTIVITY_KEY);
        return raw ? Number(raw) : 0;
    }

    private check(): void {
        if (!this.sessionService.isLoggedIn()) {
            return;
        }
        if (this.lastActivity && Date.now() - this.lastActivity >= IDLE_TIMEOUT_MS) {
            this.expireSession();
        }
    }

    private expireSession(): void {
        this.stop();
        this.messageService.add({
            key: TOAST_KEY,
            severity: 'warn',
            summary: 'Sesión cerrada',
            detail: 'Se cerró tu sesión por más de 1 hora de inactividad.',
            life: 6000
        });
        // Breve pausa para que el usuario vea el aviso antes de redirigir
        setTimeout(() => {
            this.sessionService.clearSession();
            this.router.navigate([APP_ROUTES.AUTH.LOGIN]).then(() => window.location.reload());
        }, 1200);
    }
}
